/**
 * Componente/error logs
 * 
 * @author Ronald J Llusco - 79746003<lluscovillarpando@gmail.com>
 * @version 1.0.0
 * @license MIT
 */
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { ValidatorsService } from 'src/app/services/validators.service';

@Injectable({
  providedIn: 'root'
})
export class ErrorLogsService {

  private router = inject(Router);
  private validatorsService = inject(ValidatorsService);

  logDeErrores(e: any): void {
    if (e.status === 422) {
      this.notifications(
        e?.error?.errors[0]?.message ? e?.error?.errors[0]?.field ? e?.error?.errors[0]?.field + ' - ' + e?.error?.errors[0]?.message : e?.error?.errors[0]?.message : 'Los datos no son validos, Intenta nuevamente',
        'warning', 'form');
    } else if (e.status === 401) {
      localStorage.removeItem('token');
      this.router.navigateByUrl('/auth');
      Swal.fire({
        title: 'Tu sesión expiró.',
        text: 'Vuelve a iniciar sesión para continuar',
        icon: 'info',
        showClass: { popup: 'animated animate fadeInDown' },
        customClass: { container: 'swal-alert' },
      });
    } else if (e.status === 0) {
      Swal.fire({
        title: 'Oops...',
        text: 'Ocurrió un imprevisto presiona F5 | Revisa tu conexión a internet',
        icon: 'warning',
        showClass: { popup: 'animated animate fadeInDown' },
        customClass: { container: 'swal-alert' },
      });
    } else if (e.status === 400) {
      this.notifications(
        e?.error?.message ? e?.error?.message : 'Los datos no son validos, Intenta nuevamente',
        'warning', 'form');
    } else if (e.status === 409) {
      // Conflict error handled by component
    } else if (e.status === 404) {
      Swal.fire({
        title: 'Error!',
        text: 'Error dato no encontrado | pagina y/o servicio no existente',
        icon: 'warning',
        showClass: { popup: 'animated animate fadeInDown' },
        customClass: { container: 'swal-alert' },
      });
    } else if (e.status >= 500 && e.status <= 599 && e.status !== 503 && e.status !== 504) {
      Swal.fire({
        title: 'Error!',
        text: 'Ocurrió un problema interno | Si el problema persiste notifica a soporte. ' + e.error?.message,
        icon: 'error',
        showClass: { popup: 'animated animate fadeInDown' },
        customClass: { container: 'swal-alert' },
      });
    }
    else if (e.status === 999) {
      Swal.fire({
        title: '⚠️ Respuesta Demorada',
        text: 'El servidor tardó en responder. Inténtalo nuevamente.',
        icon: 'warning',
        showClass: { popup: 'animated animate fadeInDown' },
        customClass: { container: 'swal-alert' },
      });
    } else if (e.status === 402) {
      const phone = this.validatorsService.user()?.businessWhatsapp || '59179746003';
      Swal.fire({
        title: 'Cuenta Suspendida',
        text: e.error?.message || 'Su cuenta se encuentra suspendida por falta de pago. Por favor comuníquese con el área de ventas para regularizar su suscripción.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '<i class="bx bxl-whatsapp me-1"></i> Contactar a Ventas',
        cancelButtonText: 'Entendido',
        confirmButtonColor: '#25D366',
        showClass: { popup: 'animated animate fadeInDown' },
        customClass: { container: 'swal-alert' },
      }).then((result) => {
        if (result.isConfirmed) {
          window.open(`https://wa.me/${phone}?text=` + encodeURIComponent('Hola, mi suscripción está vencida/suspendida y deseo regularizar el pago.'), '_blank');
        }
      });
    } else if (e.status == 503 || e.status === 504) {

    } else {
      Swal.fire({
        title: 'Oops...',
        text: e.error?.message ? e.error.message : 'Ocurrió un imprevisto presiona F5',
        icon: 'error',
        showClass: { popup: 'animated animate fadeInDown' },
        customClass: { container: 'swal-alert' },
      });
    }
  }

  /**
   * A function that displays a toast notification.
   * @param {string} msg - The message you want to display
   * @param {'warning' | 'error' | 'success' | 'info' |'question'} icon
   * @param {string} [target=body] - The target element to display the toast.
   */
  notifications(msg: string, icon: 'warning' | 'error' | 'success' | 'info' | 'question', target: string = 'body') {
    const isDarkMode = document.body.getAttribute('data-bs-theme') === 'dark';

    // Map icon type to accent colors
    const accentMap: Record<string, string> = {
      warning: '#f59e0b',
      error: '#ef4444',
      success: '#22c55e',
      info: '#3b82f6',
      question: '#8b5cf6',
    };
    const accent = accentMap[icon] || '#3b82f6';

    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      target: target,
      background: isDarkMode ? 'rgba(30, 41, 59, 0.97)' : 'rgba(255, 255, 255, 0.97)',
      color: isDarkMode ? '#f1f5f9' : '#1e293b',
      showClass: {
        popup: 'swal-toast-slide-in'
      },
      hideClass: {
        popup: 'swal-toast-slide-out'
      },
      customClass: {
        popup: `swal-toast-premium swal-toast-${icon}`,
        title: 'swal-toast-title',
        timerProgressBar: 'swal-toast-timer',
        icon: 'swal-toast-icon',
      },
      didOpen: (toast) => {
        // Apply accent border dynamically
        toast.style.borderLeft = `4px solid ${accent}`;
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      },
      didClose() {
        Toast.close();
      },
    });
    Toast.fire({
      icon: icon,
      title: msg
    }).then(() => {
      Swal.close();
    });
  }
}
